import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  LegalAcceptanceRecord,
  LegalAcceptResponse,
  LegalDocument,
  LegalRequirements
} from 'shared';

/**
 * Legal & Compliance Center client.
 *
 * Deliberately uncached: which documents are outstanding changes the moment the user
 * accepts, or when an operator publishes a new version (which takes effect without a
 * deploy). A stale cache here would either re-show an accepted document or, worse,
 * skip a newly published one.
 */
@Injectable({ providedIn: 'root' })
export class LegalService {
  private http = inject(HttpClient);

  /** Documents this user still has to acknowledge, in the order to display them. */
  getRequirements(): Observable<LegalRequirements> {
    return this.http.get<LegalRequirements>('/legal/requirements');
  }

  /**
   * Confirm the given documents. Versions must be the ones the user was shown — the
   * backend returns 409 if the published text changed in the meantime.
   */
  accept(documents: LegalDocument[]): Observable<LegalAcceptResponse> {
    return this.http.post<LegalAcceptResponse>('/legal/accept', {
      documents: documents.map(({ key, version }) => ({ key, version }))
    });
  }

  /** The signed-in user's own acceptance history, newest first. */
  getAcceptances(): Observable<{ acceptances: LegalAcceptanceRecord[] }> {
    return this.http.get<{ acceptances: LegalAcceptanceRecord[] }>('/legal/acceptances');
  }

  /**
   * Fetch a document body as HTML. The server returns body innerHTML only
   * (`?embed=true` is already baked into `document.url`).
   */
  getDocumentBody(document: LegalDocument): Observable<string> {
    return this.http.get(document.url, { responseType: 'text' });
  }
}
